export const PAGE_CREATED_FROM = {
    NEW: "new",
    CREATOR_COMPONENT: "creator_component",
  } as const;
  export type PageCreatedFrom =
    (typeof PAGE_CREATED_FROM)[keyof typeof PAGE_CREATED_FROM];
  type PageCreatedFromLike =
    | PageCreatedFrom
    | string
    | null
    | undefined
    | { created_from?: string | null };
  function getCreatedFromValue(value?: PageCreatedFromLike) {
    if (value && typeof value === "object" && "created_from" in value) {
      return value.created_from;
    }
    return value;
  }
  export function normalizePageCreatedFrom(
    value?: PageCreatedFromLike
  ): PageCreatedFrom {
    const normalizedValue = String(getCreatedFromValue(value) || "")
      .trim()
      .toLowerCase();
    if (normalizedValue === PAGE_CREATED_FROM.CREATOR_COMPONENT) {
      return PAGE_CREATED_FROM.CREATOR_COMPONENT;
    }
    // Backward-compatible fallback for legacy rows with no page-level source yet.
    return PAGE_CREATED_FROM.NEW;
  }
  export function isNewCreatorPage(value?: PageCreatedFromLike) {
    return normalizePageCreatedFrom(value) === PAGE_CREATED_FROM.NEW;
  }
  export function isCreatorComponentPage(value?: PageCreatedFromLike) {
    return (
      normalizePageCreatedFrom(value) === PAGE_CREATED_FROM.CREATOR_COMPONENT
    );
  }

  /** Same rules as creator page slug normalization in `createCreatorPage` / `updateCreatorPage`. */
  function normalizeCreatorPageSlugForMatch(value?: string | null) {
    return String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  /**
   * The `page_components` row copied from the user's landing carousel when the page was created
   * via an existing component. Its route/unique_id stays aligned with the page slug when the page
   * is renamed (`syncSourceComponentMetadataFromCreatorPage`).
   */
  export function isCreatorPageSourceLandingComponent(options: {
    pageCreatedFrom?: PageCreatedFromLike;
    creatorPageSlug?: string | null;
    component?: any;
  }) {
    if (!isCreatorComponentPage(options.pageCreatedFrom)) {
      return false;
    }
    const pageToken = normalizeCreatorPageSlugForMatch(options.creatorPageSlug);
    if (!pageToken) {
      return false;
    }
    const routeToken = normalizeCreatorPageSlugForMatch(
      options.component?.route_name
    );
    const uniqueToken = normalizeCreatorPageSlugForMatch(
      options.component?.unique_id
    );
    return (
      (Boolean(routeToken) && routeToken === pageToken) ||
      (Boolean(uniqueToken) && uniqueToken === pageToken)
    );
  }

  export function canEditShowAllForCreatorPage(value?: PageCreatedFromLike) {
    return isNewCreatorPage(value);
  }

  /** Instagram REELS "Show" (posts / reels / both) — only on blank creator pages, not pages copied from link-in-bio. */
  export function canEditReelsShowFilterForCreatorPage(
    value?: PageCreatedFromLike
  ) {
    return isNewCreatorPage(value);
  }

export const INSTAGRAM_REELS_FILTER_OPTIONS = ["post", "reels", "both"] as const;
export type InstagramReelsFilter =
  (typeof INSTAGRAM_REELS_FILTER_OPTIONS)[number];

function isActivePageComponent(component: any) {
  const status = String(component?.status_expiry ?? component?.status ?? "")
    .toLowerCase()
    .trim();
  return status !== "hidden" && status !== "deleted";
}

function isInstagramReelsComponent(component: any) {
  const displayName = String(component?.display_name ?? "").toLowerCase().trim();
  return (
    String(component?.type ?? "").toLowerCase() === "reels" ||
    displayName === "reels"
  );
}

export function normalizeInstagramReelsFilter(
  value?: unknown
): InstagramReelsFilter | null {
  const normalized = String(value || "").toLowerCase().trim();
  if (normalized === "content") return "reels";
  if (normalized === "products") return "post";
  if (
    INSTAGRAM_REELS_FILTER_OPTIONS.includes(
      normalized as InstagramReelsFilter
    )
  ) {
    return normalized as InstagramReelsFilter;
  }
  return null;
}

/**
 * Creator pages copied from a link-in-bio carousel are locked to that carousel's
 * Instagram Posts / Reels / Both variant (post, reels, or both).
 */
export function getLockedInstagramReelsFilterForCreatorPage(options: {
  pageCreatedFrom?: PageCreatedFromLike;
  creatorPageSlug?: string | null;
  components?: any[];
}): InstagramReelsFilter | null {
  if (!isCreatorComponentPage(options.pageCreatedFrom)) {
    return null;
  }

  const components = Array.isArray(options.components) ? options.components : [];
  const reelsComponents = components.filter((component) =>
    isInstagramReelsComponent(component)
  );

  const sourceComponent =
    reelsComponents.find((component) =>
      isCreatorPageSourceLandingComponent({
        pageCreatedFrom: options.pageCreatedFrom,
        creatorPageSlug: options.creatorPageSlug,
        component,
      })
    ) ||
    reelsComponents.find((component) => isActivePageComponent(component)) ||
    reelsComponents[0];

  if (!sourceComponent) {
    return null;
  }

  return (
    normalizeInstagramReelsFilter(
      sourceComponent?.shoppable_content_filter
    ) || "both"
  );
}

export function getAlternateInstagramReelsFilters(
  lockedFilter: InstagramReelsFilter | null
) {
  if (!lockedFilter) return [];
  return INSTAGRAM_REELS_FILTER_OPTIONS.filter(
    (filter) => filter !== lockedFilter
  );
}

export function getInstagramReelsFilterFromComponent(
  component: any
): InstagramReelsFilter | null {
  if (!isInstagramReelsComponent(component)) return null;
  return (
    normalizeInstagramReelsFilter(component?.shoppable_content_filter) || "both"
  );
}

/**
 * When creating a multipage from link-in-bio carousels, hide Instagram Posts /
 * Reels / Both variants that conflict with an existing creator page already
 * created from another variant of the same carousel family.
 */
export function getInstagramReelsFiltersBlockedByExistingCreatorPages(
  creatorPages: any[] = [],
  landingComponents: any[] = []
): InstagramReelsFilter[] {
  if (!Array.isArray(creatorPages) || !creatorPages.length) return [];

  const blocked = new Set<InstagramReelsFilter>();

  creatorPages.forEach((page) => {
    const lockedFilter = getLockedInstagramReelsFilterForCreatorPage({
      pageCreatedFrom: page?.created_from,
      creatorPageSlug: page?.slug,
      components: landingComponents,
    });
    if (!lockedFilter) return;

    getAlternateInstagramReelsFilters(lockedFilter).forEach((filter) => {
      blocked.add(filter);
    });
  });

  return Array.from(blocked);
}

export function shouldHideInstagramReelsCarouselInCreateModal(
  component: any,
  blockedFilters: InstagramReelsFilter[] = []
) {
  if (!blockedFilters.length) return false;

  const componentFilter = getInstagramReelsFilterFromComponent(component);
  if (!componentFilter) return false;

  return blockedFilters.includes(componentFilter);
}
  export function getCreatorPageCreatedFromForInsert(
    explicitCreatedFrom?: PageCreatedFromLike
  ): PageCreatedFrom {
    return normalizePageCreatedFrom(explicitCreatedFrom);
  }
  export function getCreatorPageRecordFromComponentMatches(
    pages: any[] = [],
    matchedComponents: any[] = []
  ) {
    if (!Array.isArray(pages) || !pages.length) return null;
    if (!Array.isArray(matchedComponents) || !matchedComponents.length) return null;
    const firstComponentWithPage = matchedComponents.find(
      (component: any) => component?.page_id
    );
    const directPageMatch = firstComponentWithPage
      ? pages.find((page: any) => page.id === firstComponentWithPage.page_id)
      : null;
    if (directPageMatch) {
      return directPageMatch;
    }
    return (
      pages.find((page: any) =>
        matchedComponents.some(
          (component: any) =>
            component.page_id === page.id &&
            page.expanded_component_id === component.id
        )
      ) || null
    );
  }

/**
 * When a creator page is deleted, orphaned `page_components` rows can still own
 * the public slug while the link-in-bio carousel no longer has route/unique ids.
 * Resolve the live stayuptodate carousel so `/:username/:slug` can still render
 * the normal individual component view.
 */
export function getSourceComponentForOrphanedPageComponentMatch(
  pageComponentMatches: any[] = [],
  landingComponents: any[] = []
) {
  if (!Array.isArray(pageComponentMatches) || !pageComponentMatches.length) {
    return null;
  }
  if (!Array.isArray(landingComponents) || !landingComponents.length) {
    return null;
  }

  for (const pageComponent of pageComponentMatches) {
    const routeIdentityTokens = buildIdentityTokens([
      pageComponent?.route_name,
      pageComponent?.unique_id,
    ]);

    const routeMatch = landingComponents.find((component: any) =>
      doesComponentMatchByRouteIdentity(component, routeIdentityTokens)
    );
    if (routeMatch) {
      return routeMatch;
    }

    const nameIdentityTokens = buildIdentityTokens([pageComponent?.name]);
    const nameMatch = landingComponents.find((component: any) =>
      doesComponentMatchByNameIdentity(
        component,
        nameIdentityTokens,
        pageComponent
      )
    );
    if (nameMatch) {
      return nameMatch;
    }
  }

  return null;
}

function normalizeIdentityToken(value?: any) {
  return String(value || "").trim().toLowerCase();
}

function buildIdentityTokens(values: any[] = []) {
  return new Set(
    values
      .map((value) => normalizeIdentityToken(value))
      .filter(Boolean)
  );
}

function doesComponentMatchByRouteIdentity(
  component: any,
  routeIdentityTokens: Set<string>
) {
  if (!routeIdentityTokens.size) return false;

  return [component?.route_name, component?.unique_id]
    .map((value) => normalizeIdentityToken(value))
    .some((token) => token && routeIdentityTokens.has(token));
}

function doesComponentMatchByNameIdentity(
  component: any,
  nameIdentityTokens: Set<string>,
  referenceComponent?: any
) {
  if (!nameIdentityTokens.size) return false;

  const componentName = normalizeIdentityToken(component?.name);
  if (!componentName || !nameIdentityTokens.has(componentName)) {
    return false;
  }

  const referenceType = normalizeIdentityToken(referenceComponent?.type);
  const componentType = normalizeIdentityToken(component?.type);

  return !referenceType || !componentType || referenceType === componentType;
}

export function getCreatorPageRecordForSourceComponent({
  pages = [],
  pageComponents = [],
  sourceComponent,
}: {
  pages?: any[];
  pageComponents?: any[];
  sourceComponent?: any;
}) {
  if (
    !Array.isArray(pages) ||
    !pages.length ||
    !Array.isArray(pageComponents) ||
    !pageComponents.length ||
    !sourceComponent
  ) {
    return null;
  }

  const routeIdentityTokens = buildIdentityTokens([
    sourceComponent?.route_name,
    sourceComponent?.unique_id,
  ]);
  const nameIdentityTokens = buildIdentityTokens([sourceComponent?.name]);

  let matchedPageComponents = pageComponents.filter((component: any) =>
    doesComponentMatchByRouteIdentity(component, routeIdentityTokens)
  );

  if (!matchedPageComponents.length) {
    matchedPageComponents = pageComponents
      .filter((component: any) =>
        doesComponentMatchByNameIdentity(
          component,
          nameIdentityTokens,
          sourceComponent
        )
      )
      .filter((component: any) => {
        const page = pages.find((row: any) => row.id === component?.page_id);
        return page ? isCreatorComponentPage(page?.created_from) : false;
      });
  }

  return getCreatorPageRecordFromComponentMatches(pages, matchedPageComponents);
}